import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Zap, 
  Target, 
  Sparkles, 
  Check, 
  ChevronRight,
  Users,
  BarChart3,
  Layers,
  Globe,
  Shield,
  Clock,
  TrendingUp,
  AlertCircle,
  Rocket,
  FileText,
  Link2,
  Settings,
  Play,
  Calendar,
  Search,
  MessageSquare,
  Megaphone,
  Layout,
  CheckCircle2,
  ArrowDown,
  Building2,
  Briefcase
} from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="text-xl font-bold text-slate-800 font-['Outfit'] tracking-tight">Pravik</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#problem" className="text-sm text-slate-500 hover:text-blue-600 transition-colors font-medium">Problem</a>
              <a href="#solution" className="text-sm text-slate-500 hover:text-blue-600 transition-colors font-medium">Solution</a>
              <a href="#how-it-works" className="text-sm text-slate-500 hover:text-blue-600 transition-colors font-medium">How It Works</a>
              <a href="#triggers" className="text-sm text-slate-500 hover:text-blue-600 transition-colors font-medium">Triggers</a>
            </div>
            <div className="flex items-center gap-3">
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-blue-600 text-white hover:bg-blue-700 rounded-full px-6 h-10 shadow-lg shadow-blue-600/25"
                  data-testid="nav-dashboard-btn"
                >
                  Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="text-sm h-10 text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                    data-testid="nav-login-btn"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => navigate('/signup')}
                    className="bg-blue-600 text-white hover:bg-blue-700 rounded-full px-6 h-10 shadow-lg shadow-blue-600/25 transition-all hover:shadow-xl hover:shadow-blue-600/30"
                    data-testid="nav-signup-btn"
                  >
                    Get Started Free
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-40 md:pb-28 px-6 md:px-12 lg:px-20 overflow-hidden bg-white">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 via-white to-white"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-8">
              <Zap className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Intent-driven marketing automation</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6 font-['Outfit'] leading-[1.1]">
              Turn intent signals into <span className="text-blue-600">messaging & pages that convert</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-500 mb-10 max-w-2xl mx-auto leading-relaxed">
              Pravik turns campaign, conversation, and real-world signals into segment-specific landing pages — and the matching campaign messaging — so every click hits the right version.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Button 
                size="lg"
                onClick={() => window.open('https://calendly.com/tmalik25/30min', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-12 h-16 text-lg font-semibold w-full sm:w-auto shadow-lg shadow-blue-600/25 transition-all hover:shadow-xl hover:shadow-blue-600/30 hover:-translate-y-0.5"
                data-testid="hero-cta-btn"
              >
                Book A Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            <p className="text-sm text-slate-400 flex items-center justify-center gap-4">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Magic links or snippet</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Non-destructive</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Works on any website</span>
            </p>
          </div>
          
          {/* Flow Visualization - Modern Design with Colors */}
          <div className="mt-20 max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              {/* Step 1 */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-3 transition-all group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-blue-200">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-xs font-semibold text-slate-700">Intent Signal</span>
              </div>
              
              {/* Connector */}
              <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-200 to-blue-300 mx-2 hidden sm:block rounded-full"></div>
              
              {/* Step 2 */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-3 transition-all group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-blue-200">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-xs font-semibold text-slate-700">Campaign Play</span>
              </div>
              
              {/* Connector */}
              <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-300 to-emerald-200 mx-2 hidden sm:block rounded-full"></div>
              
              {/* Step 3 */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3 transition-all group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-emerald-200">
                  <MessageSquare className="w-6 h-6 text-emerald-600" />
                </div>
                <span className="text-xs font-semibold text-slate-700">Messaging</span>
              </div>
              
              {/* Connector */}
              <div className="flex-1 h-0.5 bg-gradient-to-r from-emerald-200 to-amber-200 mx-2 hidden sm:block rounded-full"></div>
              
              {/* Step 4 */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-3 transition-all group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-amber-200">
                  <Megaphone className="w-6 h-6 text-amber-600" />
                </div>
                <span className="text-xs font-semibold text-slate-700">Ad Copy</span>
              </div>
              
              {/* Connector */}
              <div className="flex-1 h-0.5 bg-gradient-to-r from-amber-200 to-violet-200 mx-2 hidden sm:block rounded-full"></div>
              
              {/* Step 5 */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mb-3 transition-all group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-violet-200">
                  <Layout className="w-6 h-6 text-violet-600" />
                </div>
                <span className="text-xs font-semibold text-slate-700">Landing Page</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Strip */}
      <section className="py-6 px-6 md:px-12 lg:px-20 bg-slate-100/70 border-y border-slate-200/60">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-500">Built for performance teams and agencies</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span className="px-4 py-1.5 bg-white rounded-full text-sm text-slate-600 border border-slate-200 font-medium">Paid Search/Social</span>
              <span className="px-4 py-1.5 bg-white rounded-full text-sm text-slate-600 border border-slate-200 font-medium">Lifecycle</span>
              <span className="px-4 py-1.5 bg-white rounded-full text-sm text-slate-600 border border-slate-200 font-medium">Demand Gen</span>
              <span className="px-4 py-1.5 bg-white rounded-full text-sm text-slate-600 border border-slate-200 font-medium">Multi-segment</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              <AlertCircle className="w-3.5 h-3.5" />
              The Problem
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 font-['Outfit'] mb-6">
              Intent is everywhere. Your funnel treats it like it doesn't exist.
            </h2>
            <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto">
              Intent shows up across ads, SEO, chat, email, and market moments — but it gets lost when someone lands on your site. Everyone sees the same page. That's why conversion stalls.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-slate-300 transition-all hover:shadow-lg">
              <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Intent doesn't carry forward</h3>
              <p className="text-sm text-slate-500 leading-relaxed">Campaign context and "why now" signals disappear at the click.</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-slate-300 transition-all hover:shadow-lg">
              <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">One page can't sell to everyone</h3>
              <p className="text-sm text-slate-500 leading-relaxed">"Comparing options" and "ready to buy" need different CTAs.</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-slate-300 transition-all hover:shadow-lg">
              <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center mb-4">
                <Settings className="w-5 h-5 text-violet-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Execution is scattered</h3>
              <p className="text-sm text-slate-500 leading-relaxed">Ads, pages, messaging, and approvals live in different tools.</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-slate-300 transition-all hover:shadow-lg">
              <div className="w-11 h-11 rounded-xl bg-slate-200 flex items-center justify-center mb-4">
                <Clock className="w-5 h-5 text-slate-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Iteration is too slow</h3>
              <p className="text-sm text-slate-500 leading-relaxed">By the time a page ships, the campaign has moved on.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solution" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-slate-50 to-blue-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              The Solution
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 font-['Outfit'] mb-6">
              Connect signals → segments → pages.
            </h2>
            <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto">
              Pravik scans live-signals across conversations, events & campaigns, generates detailed plays and applicable segments, and produces landing-page edits you can launch quickly & safely.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-7 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-blue-200 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Campaign Plays</h3>
              <p className="text-sm text-slate-500 leading-relaxed">Segment-ready plays with scores and validation steps.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-7 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-emerald-200 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Landing Page Context</h3>
              <p className="text-sm text-slate-500 leading-relaxed">The exact hero, proof, and CTA the segment needs.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-7 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-violet-200 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Shield className="w-6 h-6 text-violet-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Non-destructive Changes</h3>
              <p className="text-sm text-slate-500 leading-relaxed">Campaign-only edits. Original page stays intact.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              <Play className="w-3.5 h-3.5" />
              How It Works
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 font-['Outfit']">
              From Intent to live in minutes.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="relative bg-gradient-to-br from-blue-50 to-blue-100/30 rounded-2xl p-6 border border-blue-100 group hover:shadow-xl hover:shadow-blue-100/50 transition-all">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-blue-600/30">01</div>
              <div className="pt-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Live Intent</h3>
                <p className="text-sm text-slate-500">Across conversations, events, campaign launch</p>
              </div>
            </div>
            
            <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100/30 rounded-2xl p-6 border border-emerald-100 group hover:shadow-xl hover:shadow-emerald-100/50 transition-all">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-emerald-600/30">02</div>
              <div className="pt-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Choose segments</h3>
                <p className="text-sm text-slate-500">Select 2-4 that move the metric</p>
              </div>
            </div>
            
            <div className="relative bg-gradient-to-br from-amber-50 to-amber-100/30 rounded-2xl p-6 border border-amber-100 group hover:shadow-xl hover:shadow-amber-100/50 transition-all">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-amber-500/30">03</div>
              <div className="pt-4">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Generate plays</h3>
                <p className="text-sm text-slate-500">Ranked playbook + landing context</p>
              </div>
            </div>
            
            <div className="relative bg-gradient-to-br from-violet-50 to-violet-100/30 rounded-2xl p-6 border border-violet-100 group hover:shadow-xl hover:shadow-violet-100/50 transition-all">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-violet-600/30">04</div>
              <div className="pt-4">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center mb-4">
                  <Rocket className="w-5 h-5 text-violet-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Launch</h3>
                <p className="text-sm text-slate-500">Magic link or snippet</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Triggers Section */}
      <section id="triggers" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
                <Zap className="w-3.5 h-3.5" />
                Triggers
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] mb-6 leading-tight">
                Turn "why now" into campaigns you can ship today.
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Pravik can surface angles from seasonal moments, news, policy changes, competitor shifts, and conversation themes — then turn them into ready-to-launch plays and landing experiences.
              </p>
              <Button 
                onClick={handleGetStarted}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-full px-8 h-12 shadow-lg shadow-amber-500/25 transition-all hover:shadow-xl hover:shadow-amber-500/30 hover:-translate-y-0.5"
              >
                Add Triggers
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-amber-500/30 transition-all hover:bg-white/10 flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Search className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Detect relevant triggers</h4>
                  <p className="text-sm text-slate-400">Surface timely angles from market signals</p>
                </div>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-blue-500/30 transition-all hover:bg-white/10 flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Recommended segment + channel</h4>
                  <p className="text-sm text-slate-400">Match triggers to the right audience</p>
                </div>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-violet-500/30 transition-all hover:bg-white/10 flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl bg-violet-500/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Rocket className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Generate play + landing context</h4>
                  <p className="text-sm text-slate-400">Ready-to-launch campaign assets</p>
                </div>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-emerald-500/30 transition-all hover:bg-white/10 flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">You approve → launch</h4>
                  <p className="text-sm text-slate-400">Stay in control of what goes live</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get - Feature Grid */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              <Layers className="w-3.5 h-3.5" />
              Features
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 font-['Outfit'] mb-6">
              What you get
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-blue-300 hover:shadow-lg transition-all group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Globe className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-slate-900 font-semibold">Website scan → structured strategy</span>
              </div>
              <p className="text-slate-500 text-sm pl-12">Personas, segments, and plays generated from your site</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-emerald-300 hover:shadow-lg transition-all group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Check className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-slate-900 font-semibold">Validation built-in</span>
              </div>
              <p className="text-slate-500 text-sm pl-12">Keyword demand, audience sizing, landing match</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-violet-300 hover:shadow-lg transition-all group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Shield className="w-4 h-4 text-violet-600" />
                </div>
                <span className="text-slate-900 font-semibold">Launch safely</span>
              </div>
              <p className="text-slate-500 text-sm pl-12">Campaign-only personalization, original stays intact</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-amber-300 hover:shadow-lg transition-all group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Layers className="w-4 h-4 text-amber-600" />
                </div>
                <span className="text-slate-900 font-semibold">Execution workflow</span>
              </div>
              <p className="text-slate-500 text-sm pl-12">Bucket List: New → Running → Done</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-pink-300 hover:shadow-lg transition-all group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-pink-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Link2 className="w-4 h-4 text-pink-600" />
                </div>
                <span className="text-slate-900 font-semibold">Consistency across the pipeline</span>
              </div>
              <p className="text-slate-500 text-sm pl-12">Ad angle ↔ landing message ↔ CTA</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 hover:border-cyan-300 hover:shadow-lg transition-all group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-cyan-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-4 h-4 text-cyan-600" />
                </div>
                <span className="text-slate-900 font-semibold">Fast iteration</span>
              </div>
              <p className="text-slate-500 text-sm pl-12">Generate more plays, swap angles, keep learning</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              <Users className="w-3.5 h-3.5" />
              Who It's For
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 font-['Outfit'] mb-6">
              Built for teams that can't wait on web dev.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-blue-200 transition-all group text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <BarChart3 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Performance Marketing Teams</h3>
              <p className="text-slate-500">Running weekly experiments across channels</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-violet-200 transition-all group text-center">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Briefcase className="w-8 h-8 text-violet-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Agencies</h3>
              <p className="text-slate-500">Managing multiple clients, segments, and offers</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-emerald-200 transition-all group text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Building2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">B2B and B2C Teams</h3>
              <p className="text-slate-500">With multiple intents hitting one page</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-700 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-indigo-500/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] mb-6 leading-tight">
            Stop guessing which message will convert.
          </h2>
          <p className="text-lg md:text-xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            Launch segment-specific landing experiences tied to real intent signals — in minutes.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button 
              size="lg"
              onClick={handleGetStarted}
              className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-10 h-14 text-lg font-semibold shadow-xl shadow-blue-900/20 w-full sm:w-auto transition-all hover:-translate-y-0.5"
              data-testid="final-cta-btn"
            >
              Create a Campaign
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="rounded-full px-10 h-14 text-lg border-white/30 text-white hover:bg-white/10 w-full sm:w-auto backdrop-blur-sm"
            >
              Book a Demo
            </Button>
          </div>
          
          <div className="flex items-center justify-center gap-6 text-blue-200 text-sm">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" />Quick setup</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" />No site rebuild</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" />Original stays intact</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-20 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-['Outfit']">Pravik</span>
            </div>
            <p className="text-slate-400 text-sm">
              © 2026 Pravik. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
